import { normalizeLang, SUPPORTED, SupportedLang } from "./languages";

/** Host -> widget: switch language without reloading the iframe. */
export const SET_LANGUAGE_TYPE = "uysot:set-language";

/** Widget -> host: the listener is installed, it is safe to send language. */
export const READY_TYPE = "uysot:ready";

export interface LanguageBridgeOptions {
  onLanguage: (lang: SupportedLang) => void;
  /** Injectable for tests; defaults to the real embedding context. */
  parent?: Window;
  self?: Window;
}

/**
 * Listens for language changes from the host page and announces readiness.
 *
 * No origin allowlist: hosts are third-party sites on many unknown domains, so
 * an allowlist would break every new integration. The payload check is the
 * boundary — the worst a hostile message can do is change the display
 * language, which the host could equally do via the URL.
 *
 * Returns a disposer that removes the listener.
 */
export function installLanguageBridge({
  onLanguage,
  parent = window.parent,
  self = window,
}: LanguageBridgeOptions): () => void {
  const handleMessage = (event: MessageEvent) => {
    const data = event.data;
    if (!data || typeof data !== "object") return;
    if ((data as { type?: unknown }).type !== SET_LANGUAGE_TYPE) return;

    const lang = normalizeLang((data as { lang?: unknown }).lang);
    // An unusable value leaves the current language alone. A host typo should
    // not visibly downgrade a working page to the fallback.
    if (!lang) return;

    onLanguage(lang);
  };

  window.addEventListener("message", handleMessage);

  // Resolves the race where a host posts the language before this listener
  // exists and the message is silently lost. Hosts can wait for this or just
  // re-post. '*' is correct: the payload is not sensitive and the host origin
  // is unknown to us.
  if (parent && parent !== self) {
    parent.postMessage({ type: READY_TYPE, supported: [...SUPPORTED] }, "*");
  }

  return () => window.removeEventListener("message", handleMessage);
}
